import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, Button } from 'antd';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRightOutlined, DownOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import './Header.css';

const links = [['/services', 'Возможности'], ['/analytics', 'Аналитика'], ['/about', 'О проекте'], ['/contacts', 'Контакты']];

export default function AppHeader() {
  const { user, isAuthenticated, logout } = useAuth();
  const { confirmDiscard, clearDirty } = useUnsavedChanges();
  const { pathname } = useLocation();
  const isHome = pathname === '/' || pathname === '/home';
  const isAuthRoute = ['/login', '/register', '/forgot-password', '/reset-password'].includes(pathname);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef(null);
  const mobileNavigation = useRef(null);
  useEffect(() => { setMenuOpen(false); }, [pathname]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    mobileNavigation.current?.querySelector('a')?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        menuButton.current?.focus();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = [menuButton.current, ...mobileNavigation.current.querySelectorAll('a[href]')].filter(Boolean);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const handleResize = () => {
      if (window.innerWidth > 900) setMenuOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);
  const accountItems = [
    { key: 'personal', label: 'Личный кабинет', icon: <UserOutlined />, onClick: () => navigate('/personal') },
    { key: 'logout', label: 'Выйти', icon: <LogoutOutlined />, onClick: () => { if (!confirmDiscard()) return; clearDirty(); logout(); navigate('/login'); } },
  ];
  if (isAuthRoute) return null;
  return (
    <header className="app-header">
      <a className="skip-link" href="#main-content">Перейти к содержимому</a>
      <div className="header-content">
        <Link to="/" className="zema-wordmark" aria-label="ЗЕМА — главная">
          <img className="zema-brand-logo" src="/zema_logo_clean.png" alt="ЗЕМА" width="76" height="65" />
          <span className="zema-wordmark-tagline">Оценка, которая<br />даёт уверенность</span>
        </Link>
        <nav className="header-navigation" aria-label="Основная навигация">{links.map(([to, label]) => <NavLink key={to} to={to}>{label}</NavLink>)}</nav>
        <div className="header-actions">
          {isAuthenticated ? <Dropdown menu={{ items: accountItems }} trigger={['click']}><Button className="header-account"><UserOutlined /><span>{user?.first_name || 'Кабинет'}</span><DownOutlined /></Button></Dropdown> : <Link to="/login" className="header-login">Войти</Link>}
          {isHome && <Link className="header-estimate" to={isAuthenticated ? '/personal' : '/login'} state={{ from: '/personal' }}><span>Оценить объект</span><ArrowRightOutlined aria-hidden="true" /></Link>}
          <button ref={menuButton} type="button" className="header-menu-toggle" aria-expanded={menuOpen} aria-controls="mobile-navigation" aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'} onClick={() => setMenuOpen(!menuOpen)}><span className={menuOpen ? 'is-open' : ''} /><span className={menuOpen ? 'is-open' : ''} /></button>
        </div>
      </div>
      {menuOpen && <>
        <div className="mobile-navigation-backdrop" aria-hidden="true" onClick={() => { setMenuOpen(false); menuButton.current?.focus(); }} />
        <nav ref={mobileNavigation} id="mobile-navigation" className="mobile-navigation" aria-label="Мобильная навигация">
          <div className="mobile-navigation__eyebrow">Навигация</div>
          <div className="mobile-navigation__links">{links.map(([to, label]) => <NavLink key={to} to={to} onClick={() => setMenuOpen(false)}><span>{label}</span><ArrowRightOutlined aria-hidden="true" /></NavLink>)}</div>
          <div className="mobile-navigation__actions">
            <Link className="mobile-navigation__primary" to={isAuthenticated ? '/personal' : '/login'} state={{ from: '/personal' }} onClick={() => setMenuOpen(false)}>Оценить объект <ArrowRightOutlined aria-hidden="true" /></Link>
            <Link className="mobile-navigation__account" to={isAuthenticated ? '/personal' : '/login'} onClick={() => setMenuOpen(false)}><UserOutlined aria-hidden="true" />{isAuthenticated ? 'Личный кабинет' : 'Войти в кабинет'}</Link>
          </div>
        </nav>
      </>}
    </header>
  );
}
